import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { ClosedWonAccount } from '@/models/salesAnalytics'

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

type SortKey = 'account' | 'repName' | 'carriers' | 'firstBoundDate' | 'firstPolicyStart' | 'premium'

const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: 'account', label: 'Account' },
  { key: 'repName', label: 'Rep' },
  { key: 'carriers', label: 'Carriers' },
  { key: 'firstBoundDate', label: 'First bound' },
  { key: 'firstPolicyStart', label: 'First policy start' },
  { key: 'premium', label: 'Premium', numeric: true },
]

const carrierList = (row: ClosedWonAccount) => row.carriers.join(', ')

// Dates are ISO strings, so comparing them as text already orders them correctly.
function compare(a: ClosedWonAccount, b: ClosedWonAccount, key: SortKey): number {
  if (key === 'premium') return a.premium - b.premium
  const text = (row: ClosedWonAccount) => (key === 'carriers' ? carrierList(row) : (row[key] ?? ''))
  return String(text(a)).localeCompare(String(text(b)), undefined, {
    sensitivity: 'base',
  })
}

export function ClosedWonDetailCard({ rows }: { rows: ClosedWonAccount[] }) {
  const [sortKey, setSortKey] = useState<SortKey>('firstBoundDate')
  const [ascending, setAscending] = useState(false)

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (ascending ? compare(a, b, sortKey) : compare(b, a, sortKey))),
    [rows, sortKey, ascending]
  )

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setAscending(!ascending)
      return
    }
    setSortKey(key)
    // Money and dates read best biggest/newest first; names read A-Z.
    setAscending(key !== 'premium' && key !== 'firstBoundDate' && key !== 'firstPolicyStart')
  }

  function sortIcon(key: SortKey) {
    if (key !== sortKey) return <ArrowUpDown className="ml-1 inline size-3 opacity-30" />
    return ascending ? (
      <ArrowUp className="ml-1 inline size-3" />
    ) : (
      <ArrowDown className="ml-1 inline size-3" />
    )
  }

  const total = rows.reduce((sum, row) => sum + row.premium, 0)
  const policies = rows.reduce((sum, row) => sum + row.policies.length, 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Every closed-won customer</CardTitle>
        <p className="text-sm text-neutral-500">
          The {rows.length} customers and {policies} policies behind the totals above — {money(total)} in all.
          A customer buying from several carriers is one row. Click any column to sort. Duplicate paper filed
          under a second business entity is already removed.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableHead
                    key={column.key}
                    className={column.numeric ? 'text-right' : ''}
                    aria-sort={sortKey === column.key ? (ascending ? 'ascending' : 'descending') : 'none'}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto px-0 font-medium"
                      onClick={() => toggleSort(column.key)}
                    >
                      {column.label}
                      {sortIcon(column.key)}
                    </Button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((row) => (
                <TableRow key={row.orgId}>
                  <TableCell>
                    <Link
                      to={`/insure/internal/customers/${row.orgId}`}
                      className="underline underline-offset-2"
                    >
                      {row.account}
                    </Link>
                  </TableCell>
                  <TableCell>{row.repName}</TableCell>
                  <TableCell>
                    {carrierList(row) || '—'}
                    {row.policies.length > 1 && (
                      <span className="text-neutral-500"> · {row.policies.length} policies</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">{row.firstBoundDate}</TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {row.firstPolicyStart ?? <span className="text-amber-700">no start date</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{money(row.premium)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
